import { useState, useEffect } from 'react'
import debounce from 'lodash/debounce'
import { Button, Modal } from '@/components/atoms'
import { useAppDispatch, useAppSelector } from '@/store'
import { addMediaTitle, setMediaOptIn, resetSalesTargetDisplayContentMedia, setSalesTargetDisplayContentMedia } from '@/store/slices/salesTargetSlice'
import { MediaCardForm } from '@/components/organisms'
import { MODAL_TYPE } from '@/utils/constants/globalStyle'
import { UploadFile } from '@/components/molecules'
import { useFileUpload } from '@/hooks/useFileUpload'
import { Constants } from '@/utils/constants/commonConstants'

function UploadMedia({ setMediaCardFormData, mediaCardFormData, uploadFor = null, MediaCardUiSchema, title, fileType, label }) {
  const dispatch = useAppDispatch()
  const initialFileErrorState = { feedbackType: 'info', helperText: '' }
  const [open, setOpen] = useState(false)
  const [mediaFile, setMediaFile] = useState([])
  const { onUploadFile } = useFileUpload()
  const [fileError, setFileError] = useState(initialFileErrorState)
  const [error, setError] = useState(false)
  const contentMedia = useAppSelector(state => state.salesTarget.displayContentMedia)
  const [uploading, setUploading] = useState(false)

  const isCloseModal = () => {
    dispatch(resetSalesTargetDisplayContentMedia())
    setMediaFile([])
    setOpen(false)
    setFileError(initialFileErrorState)
  }
  const isOpenModal = () => {
    setOpen(true)
  }

  const handleUpload = file => {
    onUploadFile(file, res => {
      setMediaFile([res.filePath])
    })
  }

  useEffect(() => {
    if (contentMedia?.imageUrl) {
      setMediaFile([contentMedia?.imageUrl])
    }
  }
    , [contentMedia?.imageUrl])

  const handleError = () => {
    setFileError({ feedbackType: 'error', helperText: 'Please use a valid PNG/JPEG/JPG or WebP file' })
  }

  const handleDelete = () => {
    dispatch(resetSalesTargetDisplayContentMedia())
    setMediaFile([])
  }

  const handleDone = () => {
    setOpen(false)
  }

  const debouncedHandleMediaCardChange = debounce((data) => {
    if (data?.title !== undefined) {
      dispatch(addMediaTitle(data?.title));
    }

    if (data?.optIn !== undefined) {
      dispatch(setMediaOptIn(data?.optIn));
    }

    setMediaCardFormData(prevData => ({
      ...prevData,
      ...data,
    }));
  }, 500);

  const handleMediaCardChange = (data) => {
    debouncedHandleMediaCardChange(data);
  }

  return (
    <>
      <h1 className="mb-4 text-sm font-bold">{title}</h1>
      {!mediaFile?.length > 0 && !contentMedia?.imageUri && (
        <div className="mb-6 flex flex-wrap py-2" data-testid="uploadMediaBtn">
          <Button
            handleButton={isOpenModal}
            uiSchema={{
              size: 'large',
              title: 'Upload Media',
              variant: 'primary',
              disabled: mediaFile.length > 0,
            }}
          />
        </div>
      )}
      <Modal
        type={MODAL_TYPE.SMALL}
        isDark={true}
        onCloseModal={isCloseModal}
        onClickSecondaryButton={isCloseModal}
        onClickPrimaryButton={handleDone}
        primaryButtonText="Done"
        showTertiaryButton={false}
        showAuxiliaryButton={false}
        showSecondaryButton={true}
        showLink={false}
        showModal={open}
        title={'Upload Media'}
        primaryButtonDisabled={
          mediaFile.length == 0 || (Object.keys(fileError).length > 0 && fileError?.feedbackType === 'error') || error
        }
        secondaryButtonDisabled={uploading}
        hideCloseButton={uploading}
        content={
          <div className="media-upload">
            <UploadFile
              uploadFor={uploadFor}
              acceptedExtensions=".jpg,.png,.jpeg,.webp"
              description={
                <span>
                  Drag and drop your file here <br /> (only .jpg,.png,.webp files) <br /> or click here to upload from My Files
                </span>
              }
              label="Upload image file"
              variant={Constants.Edit}
              buttonLabel="Select from System Library"
              onUploadError={handleError}
              feedbackType={fileError.feedbackType}
              helperText={fileError.helperText}
              type="image"
              required={true}
              imageUri={contentMedia?.imageUri}
              setFile={setMediaFile}
              uploadFileSuccess={setSalesTargetDisplayContentMedia}
              resetUploadFile={resetSalesTargetDisplayContentMedia}
              onUpload={handleUpload}
              fileSizeCheck={true}
              onSetError={setError}
              setFileErrorMessage={setFileError}
              fileType={fileType}
              uploading={uploading}
              setUploading={setUploading}
            />
          </div>
        }
      />
      {(contentMedia?.imageUri || contentMedia?.imageUrl) && (
        <MediaCardForm
          imageUri={contentMedia?.imageUri ?? contentMedia?.imageUrl}
          handleChange={handleMediaCardChange}
          handleDelete={handleDelete}
          mediaCardFormData={mediaCardFormData}
          MediaCardUiSchema={MediaCardUiSchema}
          label={label}
        />
      )}
    </>
  )
}

export default UploadMedia
